var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IconButton, Hidden, Box, Card, Link, Stack } from '@mui/material';
import { CuppingCommentIcon } from '../atoms/Icons';
import OrderedQuantity from '../atoms/OrderedQuantity';
import BidFobPriceField from '../atoms/BidFobPriceField';
import { numericColumn, column } from '../../hooks/useColumns';
import { useNotify } from '../../hooks/notificationHook';
import { validURL, weightFormat, kgFobPriceToLbFobPrice, } from '../../utils/format';
import AuctionIconLink from './AuctionIconLink';
import { HelpWithDialogIcon } from './HelpWithDialogIcon';
import SoldOutTag from './SoldOutTag';
import SampleOrderCheckbox from './SampleOrderCheckbox';
import CollapsibleTable from '../table/collapsibleTable';
import OrderedItemsCard from './OrderedItemsCard';
var CommentIconWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  position: relative;\n  width: 32px;\n  height: 32px;\n\n  & .count {\n    position: absolute;\n    width: 100%;\n    text-align: center;\n    line-height: 32px;\n    top: -2px;\n    left: 0;\n    color: white;\n    font-size: 0.8rem;\n  }\n"], ["\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  position: relative;\n  width: 32px;\n  height: 32px;\n\n  & .count {\n    position: absolute;\n    width: 100%;\n    text-align: center;\n    line-height: 32px;\n    top: -2px;\n    left: 0;\n    color: white;\n    font-size: 0.8rem;\n  }\n"])));
var OrderedItemsTable = function (_a) {
    var offerList = _a.offerList, orderedItems = _a.orderedItems, role = _a.role, maxSampleOrderCount = _a.maxSampleOrderCount, totalSampleOrderedCount = _a.totalSampleOrderedCount, totalOrderedCount = _a.totalOrderedCount, _b = _a.isSecondaryDirectSampleOrder, isSecondaryDirectSampleOrder = _b === void 0 ? false : _b, onOrderedItemChange = _a.onOrderedItemChange, onOpenCuppingComment = _a.onOpenCuppingComment, _c = _a.isUSRoaster, isUSRoaster = _c === void 0 ? false : _c;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var notify = useNotify();
    var renderFarmWithLink = React.useCallback(function (_a) {
        var offer = _a.offer;
        var url = offer.narrativeUrls[i18n.language]
            ? offer.narrativeUrls[i18n.language]
            : '/';
        if (!validURL(url))
            return offer.farm;
        return (React.createElement(Link, { href: url, target: "_blank", rel: "noopener noreferrer", download: true }, offer.farm));
    }, [i18n.language]);
    var renderStock = React.useCallback(function (orderedItem) {
        if (role !== 'admin' &&
            offerList &&
            ['expired'].includes(offerList.status))
            return '-';
        if (!orderedItem.minOrder)
            return t('labels.pending');
        var quantity = orderedItem.offer.orderType === 'auction' &&
            orderedItem.type !== 'TypicaStockItem'
            ? orderedItem.offer.quantity
            : "".concat(orderedItem.remainingQuantity, " / ").concat(orderedItem.totalQuantity);
        return "".concat(quantity, " ").concat(t('labels.stockUnit'), " (").concat(weightFormat(orderedItem.minOrder), ")");
    }, [t, role, offerList]);
    var renderComment = React.useCallback(function (orderedItem) {
        return (React.createElement(IconButton, { onClick: function () { return onOpenCuppingComment(orderedItem); } },
            React.createElement(CommentIconWrapper, null,
                React.createElement(CuppingCommentIcon, { color: "primary" }),
                React.createElement("div", { className: "count" }, orderedItem.offer.cuppingCommentsCount))));
    }, [onOpenCuppingComment]);
    var showAlertMessage = React.useCallback(function (event) {
        event.stopPropagation();
        notify('info', [t('messages.invalidOrderCombination')]);
    }, [t, notify]);
    var sampleOrderedCount = totalSampleOrderedCount ||
        orderedItems.filter(function (_a) {
            var sampleOrdered = _a.sampleOrdered;
            return sampleOrdered;
        }).length;
    var orderedCount = totalOrderedCount ||
        orderedItems.filter(function (orderedItem) { return orderedItem.orderAmount > 0; }).length;
    var renderSampleOrder = React.useCallback(function (orderedItem) {
        var checked = Boolean(orderedItem.sampleOrdered);
        var disabled = orderedCount > 0 ||
            (maxSampleOrderCount !== undefined &&
                !checked &&
                sampleOrderedCount >= maxSampleOrderCount);
        return (React.createElement(SampleOrderCheckbox, { role: role, orderedItem: orderedItem, isSecondaryDirectSampleOrder: isSecondaryDirectSampleOrder, checked: checked, disabled: disabled, onChange: function (sampleOrdered) {
                onOrderedItemChange(__assign(__assign({}, orderedItem), { sampleOrdered: sampleOrdered }));
            } }));
    }, [
        role,
        onOrderedItemChange,
        maxSampleOrderCount,
        sampleOrderedCount,
        orderedCount,
        isSecondaryDirectSampleOrder,
    ]);
    var simulatable = React.useMemo(function () { return !offerList || ['prepared', 'active'].includes(offerList.status); }, [offerList]);
    var renderOrderAmountSelect = React.useCallback(function (orderedItem) {
        if (role !== 'admin' && !simulatable) {
            return React.createElement("span", null, "-");
        }
        var offer = orderedItem.offer;
        if (offer.orderType === 'auction' && offer.bidSubmitted) {
            return React.createElement(SoldOutTag, null, t('pages.roasterOrder.bidSubmitted'));
        }
        var limit = orderedItem.remainingQuantity;
        if (orderedItem.type === 'Offer' && offer.orderType !== 'auction') {
            if (offer.purchasableCount !== undefined &&
                offer.purchasableCount !== null) {
                limit =
                    limit > offer.purchasableCount ? offer.purchasableCount : limit;
            }
            if (orderedItem.remainingQuantity <= 0)
                return React.createElement(SoldOutTag, null, "SOLD OUT");
            if (limit <= 0)
                return React.createElement(SoldOutTag, null, t('pages.roasterOrder.ordered'));
        }
        else if (orderedItem.type === 'Offer') {
            limit = offer.quantity;
        }
        var maxRangeBehavior = orderedItem.type === 'Offer' &&
            offer.orderType === 'basic' &&
            offer.purchasableCount &&
            offer.purchasableCount < offer.remainingQuantity
            ? 'alert'
            : 'disabled';
        var purchaseLimit = offer.orderType === 'auction'
            ? offer.quantity
            : offer.purchaseLimit || 0;
        var disabled = sampleOrderedCount > 0;
        var handleClick = function (event) {
            if (disabled)
                showAlertMessage(event);
        };
        return (React.createElement("span", { onClick: handleClick },
            React.createElement(OrderedQuantity, { outRangeBehavior: {
                    max: {
                        type: maxRangeBehavior,
                        message: t('errors.purchaseLimitError', {
                            limit: purchaseLimit,
                        }),
                    },
                    min: {
                        type: 'disabled',
                        message: '',
                    },
                }, max: limit, value: orderedItem.orderAmount, onChange: function (value) {
                    var nextOrderedItem = __assign(__assign({}, orderedItem), { orderAmount: +value || 0 });
                    onOrderedItemChange(nextOrderedItem);
                }, disabled: disabled })));
    }, [
        t,
        role,
        onOrderedItemChange,
        sampleOrderedCount,
        showAlertMessage,
        simulatable,
    ]);
    var renderOrderTypeIcon = React.useCallback(function (_a) {
        var offer = _a.offer;
        if (!offerList)
            return null;
        if (offer.orderType === 'auction')
            return React.createElement(AuctionIconLink, { offer: offer, isRoaster: role === 'roaster' });
        return null;
    }, [offerList, role]);
    var handleChangeFobPrice = React.useCallback(function (value, orderedItem) {
        var nextOrderedItem = __assign(__assign({}, orderedItem), { bidFobPrice: value });
        onOrderedItemChange(nextOrderedItem);
    }, [onOrderedItemChange]);
    var renderFobPrice = React.useCallback(function (orderedItem) {
        var offer = orderedItem.offer, bidFobPrice = orderedItem.bidFobPrice;
        if (!offer.fobPrice)
            return t('labels.pending');
        var offerFobPrice = isUSRoaster
            ? kgFobPriceToLbFobPrice(Number(offer.fobPrice))
            : offer.fobPrice;
        return offer.orderType === 'auction' &&
            simulatable &&
            offerList &&
            !offer.bidSubmitted ? (React.createElement(BidFobPriceField, { offer: offer, onChange: function (value) {
                return handleChangeFobPrice(value, orderedItem);
            }, value: bidFobPrice, offerFobPrice: Number(offerFobPrice) })) : (offerFobPrice);
    }, [handleChangeFobPrice, simulatable, offerList, t, isUSRoaster]);
    var isSampleOrderColumnShown = React.useMemo(function () { return role === 'admin' || simulatable; }, [role, simulatable]);
    var isOrderColumnShown = React.useMemo(function () { return role === 'admin' || simulatable; }, [role, simulatable]);
    var columns = React.useMemo(function () {
        var farmWithLinkColumn = {
            title: t('params.offer.farm'),
            render: renderFarmWithLink,
        };
        var stockColumn = {
            title: t('labels.stock'),
            render: renderStock,
        };
        var commentColumn = {
            title: ' ',
            render: renderComment,
            align: 'center',
            sorting: false,
            filtering: false,
        };
        var sampleOrderColumn = {
            title: t('pages.roasterOrder.sample'),
            render: renderSampleOrder,
            align: 'center',
            sorting: false,
            filtering: false,
        };
        var orderColumn = {
            title: t('pages.roasterOrder.orderAmount'),
            render: renderOrderAmountSelect,
            align: 'center',
            sorting: false,
            filtering: false,
        };
        var orderTypeIconColumn = {
            title: ' ',
            render: renderOrderTypeIcon,
            align: 'center',
            sorting: false,
            filtering: false,
        };
        var fobPriceColumn = numericColumn('offer.fobPrice', {
            render: renderFobPrice,
            title: (React.createElement(Box, { display: "flex", justifyContent: "flex-end" },
                React.createElement(Stack, { direction: "row", spacing: 0.5 },
                    React.createElement(Box, null, t("params.offer.".concat(isUSRoaster ? 'fobPriceUsdPerLb' : 'fobPrice'))),
                    React.createElement(HelpWithDialogIcon, { title: t('pages.offerList.FreeOnBoardHelp.title'), description: t('pages.offerList.FreeOnBoardHelp.body') })))),
            emptyValue: t('labels.pending'),
            width: '120px',
        });
        var columns = [
            column('offer.lotNumber', {
                defaultSort: 'asc',
                sorting: true,
                width: '120px',
            }),
            farmWithLinkColumn,
            column('offer.variety', {}),
            column('offer.processing', {}),
            stockColumn,
            fobPriceColumn,
            commentColumn,
        ];
        if (isSampleOrderColumnShown)
            columns.push(sampleOrderColumn);
        if (isOrderColumnShown)
            columns.push(orderColumn);
        if (offerList)
            columns.push(orderTypeIconColumn);
        return columns;
    }, [
        t,
        offerList,
        renderOrderTypeIcon,
        renderFobPrice,
        renderFarmWithLink,
        renderStock,
        renderSampleOrder,
        renderOrderAmountSelect,
        renderComment,
        isOrderColumnShown,
        isSampleOrderColumnShown,
        isUSRoaster,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Hidden, { mdDown: true },
            React.createElement(Card, { sx: { mb: 5 } },
                React.createElement(CollapsibleTable, { columns: columns, rows: orderedItems, isOrderColumnShown: isOrderColumnShown, isSampleOrderColumnShown: isSampleOrderColumnShown, renderFarmWithLink: renderFarmWithLink, renderStock: renderStock, renderComment: renderComment, renderOrderTypeIcon: renderOrderTypeIcon, renderFobPrice: renderFobPrice, renderOrderAmount: renderOrderAmountSelect, renderSampleOrder: renderSampleOrder }))),
        React.createElement(Hidden, { mdUp: true },
            React.createElement(Stack, { spacing: 5 }, orderedItems.map(function (orderedItem) {
                return (React.createElement(OrderedItemsCard, { key: orderedItem.id, orderedItem: orderedItem, isOrderColumnShown: isOrderColumnShown, isSampleOrderColumnShown: isSampleOrderColumnShown, renderFarmWithLink: renderFarmWithLink, renderStock: renderStock, renderComment: renderComment, renderOrderTypeIcon: renderOrderTypeIcon, renderFobPrice: renderFobPrice, renderOrderAmount: renderOrderAmountSelect, renderSampleOrder: renderSampleOrder, isUSRoaster: isUSRoaster }));
            })))));
};
export default React.memo(OrderedItemsTable);
var templateObject_1;
